/**
 * CRMAnalytics.jsx
 * Super Admin — CRM > Analytics
 *
 * Features:
 *  - Period selector (7D / 30D / 90D / 365D), default 30D
 *  - 12 KPI cards in 3 rows of 4
 *  - 4 charts: Daily Outreach Trend (Line), Lead Funnel (horizontal Bar),
 *    Org Type Distribution (Doughnut), Demo Funnel (Bar)
 *  - 3 Channel Performance cards: SMS, WhatsApp, Voice
 *  - All data fetched in parallel via Promise.all when period changes
 */

import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Users, MessageSquare, MessageCircle, Phone, PhoneCall,
  Clock, TrendingUp, Calendar, CalendarCheck, CheckCircle,
  Handshake, AlertCircle, BarChart2, RefreshCw,
  ArrowUpRight, ArrowDownRight, Minus,
} from 'lucide-react';
import { Bar, Line, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler,
);

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
const authHeaders = () => ({ Authorization: 'Bearer ' + localStorage.getItem('session_token') });

// ── Helpers ───────────────────────────────────────────────────────────────────

const fmt = (n) =>
  n === null || n === undefined ? '—' : Number(n).toLocaleString('en-IN');

const fmtPct = (n) =>
  n === null || n === undefined ? '—' : `${Number(n).toFixed(1)}%`;

const fmtDuration = (seconds) => {
  if (!seconds && seconds !== 0) return '—';
  const s = Number(seconds);
  const m = Math.floor(s / 60);
  const rem = Math.round(s % 60);
  return `${m}m ${rem}s`;
};

// ── Period options ────────────────────────────────────────────────────────────

const PERIODS = [
  { label: '7D',   value: '7d' },
  { label: '30D',  value: '30d' },
  { label: '90D',  value: '90d' },
  { label: '365D', value: '365d' },
];

// ── KPI card definitions (3 rows × 4) ────────────────────────────────────────

const KPI_ROWS = [
  [
    { key: 'total_contacts',        label: 'Total Contacts',             icon: Users,        color: 'blue' },
    { key: 'total_sms_sent',        label: 'Total SMS Sent',             icon: MessageSquare,color: 'yellow' },
    { key: 'whatsapp_delivery_rate',label: 'WhatsApp Delivery Rate',     icon: MessageCircle,color: 'green',  pct: true },
    { key: 'whatsapp_read_rate',    label: 'WhatsApp Read Rate',         icon: MessageCircle,color: 'emerald',pct: true },
  ],
  [
    { key: 'call_pickup_rate',      label: 'Call Pickup Rate',           icon: PhoneCall,    color: 'purple', pct: true },
    { key: 'avg_call_duration',     label: 'Avg Call Duration',          icon: Clock,        color: 'orange', dur: true },
    { key: 'total_replies',         label: 'Total Replies',              icon: MessageSquare,color: 'blue' },
    { key: 'interested_leads',      label: 'Interested Leads',           icon: TrendingUp,   color: 'green' },
  ],
  [
    { key: 'demo_requests',         label: 'Demo Requests',              icon: Calendar,     color: 'blue' },
    { key: 'demos_scheduled',       label: 'Demos Scheduled',            icon: CalendarCheck,color: 'purple' },
    { key: 'demos_completed',       label: 'Demos Completed',            icon: CheckCircle,  color: 'green' },
    { key: 'partnership_conversion_rate', label: 'Partnership Conversion Rate', icon: Handshake, color: 'yellow', pct: true },
  ],
];

const COLOR_MAP = {
  blue:    { bg: 'bg-blue-50',    icon: 'text-blue-600',    border: 'border-blue-100',    trend: 'text-blue-500' },
  green:   { bg: 'bg-green-50',   icon: 'text-green-600',   border: 'border-green-100',   trend: 'text-green-500' },
  yellow:  { bg: 'bg-yellow-50',  icon: 'text-yellow-600',  border: 'border-yellow-100',  trend: 'text-yellow-500' },
  purple:  { bg: 'bg-purple-50',  icon: 'text-[#843FEF]',   border: 'border-purple-100',  trend: 'text-purple-500' },
  emerald: { bg: 'bg-emerald-50', icon: 'text-emerald-600', border: 'border-emerald-100', trend: 'text-emerald-500' },
  orange:  { bg: 'bg-orange-50',  icon: 'text-orange-600',  border: 'border-orange-100',  trend: 'text-orange-500' },
};

// ── Sub-components ────────────────────────────────────────────────────────────

const SkeletonCard = () => (
  <div className="bg-white rounded-xl shadow-sm p-5 border border-gray-100 animate-pulse">
    <div className="flex items-center justify-between mb-3">
      <div className="w-10 h-10 rounded-lg bg-gray-200" />
      <div className="w-10 h-4 rounded bg-gray-200" />
    </div>
    <div className="w-20 h-7 rounded bg-gray-200 mb-1" />
    <div className="w-28 h-3 rounded bg-gray-100" />
  </div>
);

const KPICard = ({ label, value, icon: Icon, color, pct, dur, trend }) => {
  const c = COLOR_MAP[color] || COLOR_MAP.blue;
  let display;
  if (dur)       display = fmtDuration(value);
  else if (pct)  display = fmtPct(value);
  else           display = fmt(value);

  const TrendIcon = trend > 0 ? ArrowUpRight : trend < 0 ? ArrowDownRight : Minus;
  const trendColor = trend > 0 ? 'text-green-500' : trend < 0 ? 'text-red-500' : 'text-gray-400';

  return (
    <div className="bg-white rounded-xl shadow-sm p-5 border border-gray-100 hover:shadow-md transition-shadow">
      <div className="flex items-center justify-between mb-3">
        <div className={`w-10 h-10 rounded-lg ${c.bg} ${c.border} border flex items-center justify-center`}>
          <Icon size={18} className={c.icon} />
        </div>
        {trend !== undefined && (
          <span className={`flex items-center gap-0.5 text-xs font-semibold ${trendColor}`}>
            <TrendIcon size={13} />
          </span>
        )}
      </div>
      <p className="text-2xl font-bold text-gray-900 tracking-tight">{display}</p>
      <p className="text-xs text-gray-500 mt-0.5 font-medium">{label}</p>
    </div>
  );
};

const EmptyChart = ({ message }) => (
  <div className="h-full flex flex-col items-center justify-center text-gray-400">
    <BarChart2 size={32} className="mb-2 opacity-30" />
    <p className="text-sm">{message}</p>
  </div>
);

// ── Progress bar helper ───────────────────────────────────────────────────────

const ProgressBar = ({ label, value, max, pct, color = '#843FEF' }) => {
  const percent = max > 0 ? Math.min(100, (value / max) * 100) : (pct || 0);
  return (
    <div className="mb-3">
      <div className="flex justify-between items-center mb-1">
        <span className="text-xs text-gray-600 font-medium">{label}</span>
        <span className="text-xs font-bold text-gray-800">
          {pct !== undefined ? `${Number(pct).toFixed(1)}%` : fmt(value)}
        </span>
      </div>
      <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
        <div
          className="h-2 rounded-full transition-all duration-500"
          style={{ width: `${percent}%`, background: color }}
        />
      </div>
    </div>
  );
};

// ── Main Component ────────────────────────────────────────────────────────────

const CRMAnalytics = () => {
  const [period,          setPeriod]          = useState('30d');
  const [analytics,       setAnalytics]       = useState(null);
  const [dailyTrend,      setDailyTrend]      = useState([]);
  const [leadFunnel,      setLeadFunnel]      = useState([]);
  const [demoFunnel,      setDemoFunnel]      = useState([]);
  const [orgDistribution, setOrgDistribution] = useState([]);
  const [channelPerf,     setChannelPerf]     = useState(null);
  const [loading,         setLoading]         = useState(true);
  const [error,           setError]           = useState(null);

  const fetchAll = useCallback(async (p) => {
    setLoading(true);
    setError(null);
    try {
      const headers = authHeaders();
      const [
        analyticsRes,
        trendRes,
        leadFunnelRes,
        demoFunnelRes,
        orgRes,
        channelRes,
      ] = await Promise.all([
        axios.get(`${API_BASE_URL}/api/crm/analytics/kpis?period=${p}`,                { headers }),
        axios.get(`${API_BASE_URL}/api/crm/analytics/daily-trend?period=${p}`,          { headers }),
        axios.get(`${API_BASE_URL}/api/crm/analytics/lead-funnel`,                      { headers }),
        axios.get(`${API_BASE_URL}/api/crm/analytics/demo-funnel`,                      { headers }),
        axios.get(`${API_BASE_URL}/api/crm/analytics/org-distribution`,                 { headers }),
        axios.get(`${API_BASE_URL}/api/crm/analytics/channel-performance?period=${p}`, { headers }),
      ]);
      setAnalytics(analyticsRes.data?.data?.kpis || analyticsRes.data?.data || analyticsRes.data || {});
      setDailyTrend(trendRes.data?.data?.trend || trendRes.data?.data || trendRes.data || []);
      setLeadFunnel(leadFunnelRes.data?.data?.funnel || leadFunnelRes.data?.funnel || leadFunnelRes.data || []);
      setDemoFunnel(demoFunnelRes.data?.data?.funnel || demoFunnelRes.data?.funnel || demoFunnelRes.data || []);
      setOrgDistribution(orgRes.data?.data?.distribution || orgRes.data?.distribution || orgRes.data || []);
      setChannelPerf(channelRes.data?.data || channelRes.data || null);
    } catch (err) {
      console.error('[CRMAnalytics] fetch error:', err);
      setError(err.response?.data?.message || err.message || 'Failed to load analytics data.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(period); }, [period, fetchAll]);

  // ── Chart data builders ───────────────────────────────────────────────────

  const buildDailyTrendData = () => {
    const safeData = Array.isArray(dailyTrend) ? dailyTrend : [];
    const sorted = [...safeData].sort((a, b) => new Date(a.date) - new Date(b.date));
    const labels = sorted.map((d) =>
      new Date(d.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })
    );
    return {
      labels,
      datasets: [
        {
          label: 'SMS',
          data: sorted.map((d) => d.sms || 0),
          borderColor: '#8B5CF6',
          backgroundColor: 'rgba(139,92,246,0.08)',
          fill: true,
          tension: 0.4,
          pointRadius: 3,
          pointBackgroundColor: '#8B5CF6',
          borderWidth: 2,
        },
        {
          label: 'WhatsApp',
          data: sorted.map((d) => d.whatsapp || 0),
          borderColor: '#10B981',
          backgroundColor: 'rgba(16,185,129,0.08)',
          fill: true,
          tension: 0.4,
          pointRadius: 3,
          pointBackgroundColor: '#10B981',
          borderWidth: 2,
        },
        {
          label: 'Voice',
          data: sorted.map((d) => d.voice || 0),
          borderColor: '#3B82F6',
          backgroundColor: 'rgba(59,130,246,0.08)',
          fill: true,
          tension: 0.4,
          pointRadius: 3,
          pointBackgroundColor: '#3B82F6',
          borderWidth: 2,
        },
      ],
    };
  };

  const buildLeadFunnelData = () => {
    const FUNNEL_ORDER = [
      'new', 'contacted', 'interested', 'follow_up', 'demo_requested',
      'demo_scheduled', 'demo_completed', 'negotiation', 'converted', 'not_interested', 'unqualified',
    ];
    const safeData = Array.isArray(leadFunnel) ? leadFunnel : [];
    const sorted = [...safeData].sort((a, b) => {
      const ai = FUNNEL_ORDER.indexOf((a.status || '').toLowerCase());
      const bi = FUNNEL_ORDER.indexOf((b.status || '').toLowerCase());
      return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
    });
    const total = sorted.reduce((s, d) => s + Number(d.count || 0), 0) || 1;
    const colors = sorted.map((_, i) => {
      const ratio = i / Math.max(sorted.length - 1, 1);
      // Gradient from blue (#3B82F6) to green (#10B981)
      const r = Math.round(59  + (16  - 59)  * ratio);
      const g = Math.round(130 + (185 - 130) * ratio);
      const bl= Math.round(246 + (129 - 246) * ratio);
      return `rgba(${r},${g},${bl},0.8)`;
    });
    return {
      labels: sorted.map((d) => d.status || 'Unknown'),
      datasets: [{
        label: 'Leads',
        data: sorted.map((d) => Number(d.count || 0)),
        backgroundColor: colors,
        borderColor: colors.map((c) => c.replace('0.8', '1')),
        borderWidth: 1,
        borderRadius: 4,
      }],
    };
  };

  const buildOrgDistData = () => {
    const ORG_COLORS = ['#3B82F6', '#843FEF', '#10B981', '#F97316', '#F59E0B', '#EF4444'];
    const safeData = Array.isArray(orgDistribution) ? orgDistribution : [];
    return {
      labels: safeData.map((d) => d.type || 'Unknown'),
      datasets: [{
        data: safeData.map((d) => Number(d.count || 0)),
        backgroundColor: ORG_COLORS.slice(0, safeData.length),
        borderColor: '#ffffff',
        borderWidth: 2,
        hoverBorderWidth: 3,
      }],
    };
  };

  const buildDemoFunnelData = () => {
    const safeData = Array.isArray(demoFunnel) ? demoFunnel : [];
    return {
      labels: safeData.map((d) => d.status || 'Unknown'),
      datasets: [{
        label: 'Demos',
        data: safeData.map((d) => Number(d.count || 0)),
        backgroundColor: 'rgba(132,63,239,0.75)',
        borderColor: '#843FEF',
        borderWidth: 1,
        borderRadius: 4,
      }],
    };
  };

  // ── Chart options ─────────────────────────────────────────────────────────

  const lineOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'top', labels: { usePointStyle: true, boxWidth: 8, font: { size: 11 } } },
      tooltip: { mode: 'index', intersect: false },
    },
    scales: {
      x: { grid: { display: false }, ticks: { font: { size: 10 }, maxTicksLimit: 10 } },
      y: { beginAtZero: true, grid: { color: '#F3F4F6' }, ticks: { font: { size: 10 } } },
    },
  };

  const hBarOptions = {
    indexAxis: 'y',
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: { mode: 'nearest' },
    },
    scales: {
      x: { beginAtZero: true, grid: { color: '#F3F4F6' }, ticks: { font: { size: 10 } } },
      y: { grid: { display: false }, ticks: { font: { size: 10 } } },
    },
  };

  const doughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'right', labels: { usePointStyle: true, boxWidth: 8, font: { size: 11 } } },
    },
    cutout: '62%',
  };

  const vBarOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: { mode: 'index', intersect: false },
    },
    scales: {
      x: { grid: { display: false }, ticks: { font: { size: 10 } } },
      y: { beginAtZero: true, grid: { color: '#F3F4F6' }, ticks: { font: { size: 10 } } },
    },
  };

  // ── Channel performance data helpers ─────────────────────────────────────

  const sms   = channelPerf?.sms   || {};
  const wa    = channelPerf?.whatsapp || {};
  const voice = channelPerf?.voice  || {};

  const smsDlvRate  = sms.total_sent   > 0 ? (sms.delivered   / sms.total_sent)   * 100 : 0;
  const waDlvRate   = wa.total_sent    > 0 ? (wa.delivered    / wa.total_sent)    * 100 : 0;
  const waReadRate  = wa.total_sent    > 0 ? (wa.read         / wa.total_sent)    * 100 : 0;
  const pickupRate  = voice.total_calls > 0 ? (voice.answered / voice.total_calls)* 100 : 0;

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-gray-100 p-6">

      {/* ── Header ── */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-purple-100 border border-purple-200 flex items-center justify-center">
            <BarChart2 size={18} className="text-[#843FEF]" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900 tracking-tight">CRM Analytics</h1>
            <p className="text-xs text-gray-400 mt-0.5">Outreach performance &amp; lead pipeline insights</p>
          </div>
        </div>

        {/* Period selector + refresh */}
        <div className="flex items-center gap-2">
          <div className="flex items-center bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm">
            {PERIODS.map((p) => (
              <button
                key={p.value}
                onClick={() => setPeriod(p.value)}
                className={`px-3 py-1.5 text-xs font-semibold transition-colors ${
                  period === p.value
                    ? 'bg-[#843FEF] text-white'
                    : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>
          <button
            onClick={() => fetchAll(period)}
            disabled={loading}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-[#843FEF] text-white text-xs font-semibold rounded-lg hover:bg-purple-700 disabled:opacity-60 transition-colors shadow-sm"
          >
            <RefreshCw size={13} className={loading ? 'animate-spin' : ''} />
            Refresh
          </button>
        </div>
      </div>

      {/* ── Error banner ── */}
      {error && (
        <div className="flex items-center gap-3 bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 mb-5 text-sm">
          <AlertCircle size={16} className="flex-shrink-0" />
          <span>{error}</span>
          <button onClick={() => fetchAll(period)} className="ml-auto text-xs font-semibold underline hover:no-underline">
            Retry
          </button>
        </div>
      )}

      {/* ── KPI Cards ── */}
      {loading ? (
        <div className="space-y-4 mb-6">
          {[0, 1, 2].map((row) => (
            <div key={row} className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {[0, 1, 2, 3].map((col) => <SkeletonCard key={col} />)}
            </div>
          ))}
        </div>
      ) : (
        <div className="space-y-4 mb-6">
          {KPI_ROWS.map((row, ri) => (
            <div key={ri} className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {row.map((card) => (
                <KPICard
                  key={card.key}
                  label={card.label}
                  value={analytics?.[card.key]}
                  icon={card.icon}
                  color={card.color}
                  pct={card.pct}
                  dur={card.dur}
                />
              ))}
            </div>
          ))}
        </div>
      )}

      {/* ── Charts 2×2 ── */}
      {!loading && (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-5 mb-6">

          {/* 1. Daily Outreach Trend */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
            <h2 className="text-sm font-semibold text-gray-800 mb-4">Daily Outreach Trend</h2>
            {dailyTrend.length === 0 ? (
              <div className="h-64"><EmptyChart message="No trend data for selected period." /></div>
            ) : (
              <div className="h-64">
                <Line data={buildDailyTrendData()} options={lineOptions} />
              </div>
            )}
          </div>

          {/* 2. Lead Funnel (horizontal bar) */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
            <h2 className="text-sm font-semibold text-gray-800 mb-4">Lead Funnel</h2>
            {leadFunnel.length === 0 ? (
              <div className="h-64"><EmptyChart message="No lead funnel data available." /></div>
            ) : (
              <div className="h-64">
                <Bar data={buildLeadFunnelData()} options={hBarOptions} />
              </div>
            )}
          </div>

          {/* 3. Org Type Distribution (Doughnut) */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
            <h2 className="text-sm font-semibold text-gray-800 mb-4">Organization Type Distribution</h2>
            {orgDistribution.length === 0 ? (
              <div className="h-64"><EmptyChart message="No organization data available." /></div>
            ) : (
              <div className="h-64">
                <Doughnut data={buildOrgDistData()} options={doughnutOptions} />
              </div>
            )}
          </div>

          {/* 4. Demo Funnel (vertical bar) */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
            <h2 className="text-sm font-semibold text-gray-800 mb-4">Demo Funnel</h2>
            {demoFunnel.length === 0 ? (
              <div className="h-64"><EmptyChart message="No demo funnel data available." /></div>
            ) : (
              <div className="h-64">
                <Bar data={buildDemoFunnelData()} options={vBarOptions} />
              </div>
            )}
          </div>

        </div>
      )}

      {/* ── Channel Performance Cards ── */}
      {!loading && channelPerf && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">

          {/* SMS Performance */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 rounded-lg bg-yellow-50 border border-yellow-100 flex items-center justify-center">
                <MessageSquare size={15} className="text-yellow-600" />
              </div>
              <h3 className="text-sm font-semibold text-gray-800">SMS Performance</h3>
            </div>
            <div className="grid grid-cols-3 gap-2 mb-4">
              {[
                { label: 'Total Sent',  value: fmt(sms.total_sent) },
                { label: 'Delivered',   value: fmt(sms.delivered) },
                { label: 'Failed',      value: fmt(sms.failed) },
              ].map((s) => (
                <div key={s.label} className="bg-gray-50 rounded-lg p-2 text-center">
                  <p className="text-base font-bold text-gray-900">{s.value}</p>
                  <p className="text-[10px] text-gray-500 font-medium mt-0.5">{s.label}</p>
                </div>
              ))}
            </div>
            <ProgressBar
              label="Delivery Rate"
              pct={smsDlvRate}
              max={100}
              color="#F59E0B"
            />
          </div>

          {/* WhatsApp Performance */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 rounded-lg bg-green-50 border border-green-100 flex items-center justify-center">
                <MessageCircle size={15} className="text-green-600" />
              </div>
              <h3 className="text-sm font-semibold text-gray-800">WhatsApp Performance</h3>
            </div>
            <div className="grid grid-cols-4 gap-1.5 mb-4">
              {[
                { label: 'Sent',      value: fmt(wa.total_sent) },
                { label: 'Delivered', value: fmt(wa.delivered) },
                { label: 'Read',      value: fmt(wa.read) },
                { label: 'Replied',   value: fmt(wa.replied) },
              ].map((s) => (
                <div key={s.label} className="bg-gray-50 rounded-lg p-2 text-center">
                  <p className="text-sm font-bold text-gray-900">{s.value}</p>
                  <p className="text-[10px] text-gray-500 font-medium mt-0.5">{s.label}</p>
                </div>
              ))}
            </div>
            <ProgressBar label="Delivery Rate" pct={waDlvRate} max={100} color="#10B981" />
            <ProgressBar label="Read Rate"     pct={waReadRate} max={100} color="#059669" />
          </div>

          {/* Voice Performance */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 rounded-lg bg-purple-50 border border-purple-100 flex items-center justify-center">
                <Phone size={15} className="text-[#843FEF]" />
              </div>
              <h3 className="text-sm font-semibold text-gray-800">Voice Performance</h3>
            </div>
            <div className="grid grid-cols-2 gap-2 mb-4">
              {[
                { label: 'Total Calls',   value: fmt(voice.total_calls) },
                { label: 'Answered',      value: fmt(voice.answered) },
                { label: 'Not Answered',  value: fmt(voice.not_answered) },
                { label: 'Completed',     value: fmt(voice.completed) },
              ].map((s) => (
                <div key={s.label} className="bg-gray-50 rounded-lg p-2 text-center">
                  <p className="text-base font-bold text-gray-900">{s.value}</p>
                  <p className="text-[10px] text-gray-500 font-medium mt-0.5">{s.label}</p>
                </div>
              ))}
            </div>
            <ProgressBar label="Pickup Rate" pct={pickupRate} max={100} color="#843FEF" />
            <div className="flex items-center justify-between mt-2 pt-2 border-t border-gray-100">
              <span className="text-xs text-gray-500 font-medium flex items-center gap-1">
                <Clock size={12} /> Avg Duration
              </span>
              <span className="text-xs font-bold text-gray-800">
                {fmtDuration(voice.avg_duration)}
              </span>
            </div>
          </div>

        </div>
      )}

      {/* Empty channel state */}
      {!loading && !channelPerf && !error && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8 flex flex-col items-center text-gray-400">
          <BarChart2 size={36} className="mb-2 opacity-30" />
          <p className="text-sm">No channel performance data for the selected period.</p>
        </div>
      )}

    </div>
  );
};

export default CRMAnalytics;
